import { useState, useEffect, useCallback, useRef } from "react";
import PropTypes from "prop-types";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  Typography,
  Checkbox,
  FormControlLabel,
  IconButton,
  Tab,
  Tabs,
  Box,
  Tooltip,
  TextField,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import AddOutlinedIcon from "@mui/icons-material/AddOutlined";
import DeleteIcon from "../../common/Icons/DeleteIcon";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker } from "@mui/x-date-pickers/DesktopDatePicker";
import { toast } from "react-toastify";
import CurrencyTextbox from "../../common/CurrencyTextbox/CurrencyTextbox";
import InlineMessage from "../../common/InlineMessage/InlineMessage";
import MultiSelectDropDown from "../../common/MultiSelectDropDown/MultiSelectDropDown";
import TabPanel from "../../common/TabPanel/TabPanel";
import FooterButtons from "../../common/FooterButtons/FooterButtons";
import {
  SubscriptionPlanSchema,
  EnterpriseSubscriptionPlanSchema,
} from "../../common/ValidationSchema/AddInsSchema";
import "./SubscriptionAddInSection.css";

// Constants
const COST_TYPE_OPTIONS = [
  { value: 1, name: "Free" },
  { value: 2, name: "Paid" },
];

const FREQUENCY_OPTIONS = [
  { value: 1, label: "Monthly" },
  { value: 2, label: "Quarterly" },
  { value: 3, label: "Semi-Annual" },
  { value: 4, label: "Annual" },
  { value: 99, label: "One-Time" },
];

const TRIAL_DURATION_OPTIONS = [
  { value: 7, label: "7 Days" },
  { value: 14, label: "14 Days" },
  { value: 30, label: "30 Days" },
  { value: 60, label: "60 Days" },
  { value: 90, label: "90 Days" },
];

// Trial Duration Enum - Standard values
export const TrialDurationEnum = {
  SEVEN_DAYS: 7,
  FOURTEEN_DAYS: 14,
  THIRTY_DAYS: 30,
  SIXTY_DAYS: 60,
  NINETY_DAYS: 90,
};

// Recurring frequencies that support trial (use Set for O(1) lookups)
const RECURRING_FREQUENCIES = new Set([1, 2, 3, 4]);

// Application to enum values
const APPLICABLE_TO_SUBSCRIBER = 1;
const APPLICABLE_TO_ENTERPRISE = 2;
const ALL_ENTERPRISE_GROUPS = "All Enterprise Groups";

// Default subscription row
const getDefaultSubscriptionRow = (isEnterprise = false) => ({
  id: Date.now(),
  frequency: "",
  baseValue: "",
  effectiveFromDate: null,
  effectiveToDate: null,
  autoRenewal: true,
  isRecurring: true,
  enableTrial: false,
  trialDurationDays: null, // Use null instead of empty string
  hasActiveSubscriptions: false,
  ...(isEnterprise && {
    enterpriseGroupIds: [ALL_ENTERPRISE_GROUPS],
  }),
});

const normalizeEnterpriseGroupIds = (groupIds) => {
  const ids = Array.isArray(groupIds) ? groupIds : [];

  if (ids.length === 0) {
    return [ALL_ENTERPRISE_GROUPS];
  }

  const specificIds = ids.filter((id) => id !== ALL_ENTERPRISE_GROUPS);
  if (specificIds.length > 0) {
    return specificIds;
  }

  return [ALL_ENTERPRISE_GROUPS];
};

const getComparableEnterpriseGroupIds = (groupIds) => {
  if (!Array.isArray(groupIds) || groupIds.length === 0) {
    return [];
  }

  return normalizeEnterpriseGroupIds(groupIds);
};

const hasOverlappingEnterpriseGroups = (groupIdsA, groupIdsB) => {
  if (groupIdsA.length === 0 || groupIdsB.length === 0) {
    return false;
  }

  if (
    groupIdsA.includes(ALL_ENTERPRISE_GROUPS) ||
    groupIdsB.includes(ALL_ENTERPRISE_GROUPS)
  ) {
    return true;
  }

  const comparisonSet = new Set(groupIdsB);
  return groupIdsA.some((groupId) => comparisonSet.has(groupId));
};

const hasEnterpriseGroupFrequencyConflict = (
  plans,
  currentIndex,
  candidateGroupIds,
  candidateFrequency,
) => {
  const normalizedGroupIds = getComparableEnterpriseGroupIds(candidateGroupIds);
  const normalizedFrequency = Number(candidateFrequency);

  if (
    normalizedGroupIds.length === 0 ||
    !normalizedFrequency ||
    !Array.isArray(plans)
  ) {
    return false;
  }

  return plans.some((plan, index) => {
    if (index === currentIndex) {
      return false;
    }

    const planGroupIds = getComparableEnterpriseGroupIds(
      plan.enterpriseGroupIds,
    );
    const planFrequency = Number(plan.frequency);

    return (
      planFrequency === normalizedFrequency &&
      hasOverlappingEnterpriseGroups(normalizedGroupIds, planGroupIds)
    );
  });
};

// Get available frequency options for a subscription row (exclude frequencies used by other rows)
const getAvailableFrequencyOptions = (
  plans,
  currentIndex,
  currentFrequency,
) => {
  // Get frequencies used by other rows (excluding current row) as a Set for O(1) checks
  const usedFrequencies = new Set(
    plans
      .filter((_, idx) => idx !== currentIndex)
      .map((plan) => plan.frequency)
      .filter((freq) => freq !== "" && freq !== null && freq !== undefined),
  );

  // Filter options to exclude used frequencies, but always include current row's selected frequency
  return FREQUENCY_OPTIONS.filter(
    (option) =>
      !usedFrequencies.has(option.value) || option.value === currentFrequency,
  );
};

const getAvailableEnterpriseFrequencyOptions = (
  plans,
  currentIndex,
  currentFrequency,
  currentGroupIds,
) => {
  const normalizedGroupIds = getComparableEnterpriseGroupIds(currentGroupIds);

  const usedFrequencies = new Set(
    plans
      .filter((_, idx) => idx !== currentIndex)
      .filter((plan) => {
        const planGroupIds = getComparableEnterpriseGroupIds(
          plan.enterpriseGroupIds,
        );
        return hasOverlappingEnterpriseGroups(normalizedGroupIds, planGroupIds);
      })
      .map((plan) => plan.frequency)
      .filter((freq) => freq !== "" && freq !== null && freq !== undefined),
  );

  return FREQUENCY_OPTIONS.filter(
    (option) =>
      !usedFrequencies.has(option.value) || option.value === currentFrequency,
  );
};

// Check if more subscription rows can be added (based on available frequencies)
const canAddMoreRows = (plans) => {
  // Get all frequencies currently used in existing plans
  const usedFrequencies = new Set(
    plans
      .map((plan) => plan.frequency)
      .filter((freq) => freq !== "" && freq !== null && freq !== undefined),
  );

  // Can add more rows if we haven't used all available frequencies
  return usedFrequencies.size < FREQUENCY_OPTIONS.length;
};

// Get trial duration options based on frequency
const getTrialDurationOptions = (frequency) => {
  // For monthly frequency, only allow 30 days and more
  if (frequency === 1) {
    return TRIAL_DURATION_OPTIONS.filter((option) => option.value <= 30);
  }
  // For quarterly, semi-annual, and annual, allow all options
  return TRIAL_DURATION_OPTIONS;
};

/*istanbul ignore next */
const SubscriptionAddInSection = ({
  formik,
  isEditMode,
  isPublished,
  hasActiveSubscriptions,
  enterpriseGroupOptions = [],
  searchEnterpriseGroups,
  onValidationChange,
  onValidateActiveTab, // New prop for active tab validation
  expanded,
  onExpandChange,
  showStepFooter = false,
  stepFooterProps = {},
}) => {
  const [activeTab, setActiveTab] = useState(0);
  const [searchedEnterpriseGroups, setSearchedEnterpriseGroups] = useState([]);
  const [isSearchingGroups, setIsSearchingGroups] = useState(false);
  const [touchedFields, setTouchedFields] = useState({});
  const [enterpriseSearchText, setEnterpriseSearchText] = useState("");
  const searchTimeoutRef = useRef(null);

  // Debounced search for enterprise groups
  const handleEnterpriseGroupSearch = useCallback(
    (inputValue, actionMeta) => {
      // Only search on input-change action
      if (actionMeta?.action !== "input-change" || !searchEnterpriseGroups) {
        return;
      }

      // Track search text
      setEnterpriseSearchText(inputValue || "");

      // Clear previous timeout
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }

      // Debounce the search
      searchTimeoutRef.current = setTimeout(async () => {
        if (inputValue && inputValue.length >= 2) {
          setIsSearchingGroups(true);
          try {
            const results = await searchEnterpriseGroups(inputValue);
            setSearchedEnterpriseGroups(results);
          } catch (error) {
            console.error("Error searching enterprise groups:", error);
          } finally {
            setIsSearchingGroups(false);
          }
        } else if (!inputValue) {
          setSearchedEnterpriseGroups([]);
        }
      }, 300);
    },
    [searchEnterpriseGroups],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Get combined enterprise group options (searched + selected)
  const getEnterpriseGroupOptionsForRow = (
    selectedGroupIds,
    currentIndex,
    allPlans,
    currentFrequency,
  ) => {
    const normalizedSelectedGroupIds =
      normalizeEnterpriseGroupIds(selectedGroupIds);

    const isGroupOptionDisabled = (groupId) => {
      if (normalizedSelectedGroupIds.includes(groupId)) {
        return false;
      }

      const nextGroupIds =
        groupId === ALL_ENTERPRISE_GROUPS
          ? [ALL_ENTERPRISE_GROUPS]
          : [
            ...normalizedSelectedGroupIds.filter(
              (id) => id !== ALL_ENTERPRISE_GROUPS,
            ),
            groupId,
          ];

      return hasEnterpriseGroupFrequencyConflict(
        allPlans,
        currentIndex,
        nextGroupIds,
        currentFrequency,
      );
    };

    // Convert selectedGroupIds to options for processing
    const selectedOptions = normalizedSelectedGroupIds.map((id) => {
      // Check if it's "All Enterprise Groups"
      if (id === ALL_ENTERPRISE_GROUPS) {
        return {
          value: ALL_ENTERPRISE_GROUPS,
          label: ALL_ENTERPRISE_GROUPS,
          isDisabled: false,
        };
      }

      // Find the option from existing enterprise group options
      const existingOption = enterpriseGroupOptions.find(
        (option) => option.value === id,
      );
      if (existingOption) {
        return { ...existingOption, isDisabled: false };
      }

      // Find from searched results
      const searchedOption = searchedEnterpriseGroups.find(
        (option) => option.value === id,
      );
      if (searchedOption) {
        return { ...searchedOption, isDisabled: false };
      }

      // Fallback - create basic option
      return { value: id, label: `Group ${id}`, isDisabled: false };
    });

    // If search text is empty, show only "All Enterprise Groups" option
    if (!enterpriseSearchText || enterpriseSearchText.trim() === "") {
      const allOption = {
        value: ALL_ENTERPRISE_GROUPS,
        label: ALL_ENTERPRISE_GROUPS,
        isDisabled: isGroupOptionDisabled(ALL_ENTERPRISE_GROUPS),
      };
      // Include selected groups that are not "All Enterprise Groups" to keep them visible
      const selectedNonAll = selectedOptions.filter(
        (option) => option.value !== ALL_ENTERPRISE_GROUPS,
      );
      return [allOption, ...selectedNonAll];
    }

    const searchedOptions =
      searchedEnterpriseGroups.length > 0 ? searchedEnterpriseGroups : [];

    // Merge selected groups with filtered search results to ensure selected items are always visible
    const selectedValues = new Set(
      selectedOptions.map((option) => option.value),
    );
    const mergedOptions = [...selectedOptions];

    searchedOptions.forEach((option) => {
      if (!selectedValues.has(option.value)) {
        mergedOptions.push({
          ...option,
          isDisabled: isGroupOptionDisabled(option.value),
        });
      }
    });

    // Filter out "All Enterprise Groups" from options when searching
    return mergedOptions.filter(
      (option) => option.value !== ALL_ENTERPRISE_GROUPS,
    );
  };

  // Handle enterprise group selection with logic to remove "All" when selecting specific groups
  const handleEnterpriseGroupSelection = (planType, index, selectedIds) => {
    const finalSelection = normalizeEnterpriseGroupIds(selectedIds);

    updateSubscriptionField(
      planType,
      index,
      "enterpriseGroupIds",
      finalSelection,
    );
  };

  // Get applicable to values from formik
  const applicableTo = formik.values.applicableTo || [];
  const hasSubscriber = applicableTo.includes(APPLICABLE_TO_SUBSCRIBER);
  const hasEnterprise = applicableTo.includes(APPLICABLE_TO_ENTERPRISE);

  // Show tabs based on applicable to
  const showSubscriberTab = hasSubscriber;
  const showEnterpriseTab = hasEnterprise;

  // Initialize subscription data if not present
  useEffect(() => {
    if (!formik.values.subscriptionDetails) {
      formik.setFieldValue("subscriptionDetails", {
        costType: 1,
        subscriberPlans: [],
        enterprisePlans: [],
      });
    }
  }, []);

  // Auto-add default row when Applicable To is selected
  useEffect(() => {
    const currentSubscriptionDetails = formik.values.subscriptionDetails || {
      costType: 1,
      subscriberPlans: [],
      enterprisePlans: [],
    };

    // Only auto-add rows if cost type is paid (costType: 2)
    if (currentSubscriptionDetails.costType === 2) {
      // Add default subscriber plan row when Subscriber is selected and no plans exist
      if (
        hasSubscriber &&
        currentSubscriptionDetails.subscriberPlans.length === 0
      ) {
        formik.setFieldValue("subscriptionDetails.subscriberPlans", [
          getDefaultSubscriptionRow(false),
        ]);
      }

      // Add default enterprise plan row when Enterprise is selected and no plans exist
      if (
        hasEnterprise &&
        currentSubscriptionDetails.enterprisePlans.length === 0
      ) {
        formik.setFieldValue("subscriptionDetails.enterprisePlans", [
          getDefaultSubscriptionRow(true),
        ]);
      }
    }
  }, [
    hasSubscriber,
    hasEnterprise,
    formik.values.subscriptionDetails?.costType,
  ]);

  // Get subscription details from formik
  const subscriptionDetails = formik.values.subscriptionDetails || {
    costType: 1,
    subscriberPlans: [],
    enterprisePlans: [],
  };

  const {
    costType,
    subscriberPlans = [],
    enterprisePlans = [],
  } = subscriptionDetails;

  // Ensure arrays are always valid arrays
  const safeSubscriberPlans = Array.isArray(subscriberPlans)
    ? subscriberPlans
    : [];
  const safeEnterprisePlans = Array.isArray(enterprisePlans)
    ? enterprisePlans
    : [];

  // Cleanup effect to fix any existing trialDuration empty strings
  useEffect(() => {
    const cleanupTrialDuration = (plans, planType) => {
      // Safety check: ensure plans array exists and is actually an array
      if (!plans || !Array.isArray(plans) || plans.length === 0) {
        return;
      }

      let needsUpdate = false;
      plans.forEach((plan) => {
        if (
          plan.trialDurationDays === "" ||
          (plan.enableTrial === false && plan.trialDurationDays !== null)
        ) {
          needsUpdate = true;
        }
      });

      // Only update if necessary to avoid infinite loops
      if (needsUpdate) {
        const updatedPlans = plans.map((plan) => {
          if (
            plan.trialDurationDays === "" ||
            (plan.enableTrial === false && plan.trialDurationDays !== null)
          ) {
            return { ...plan, trialDurationDays: null };
          }
          return plan;
        });
        formik.setFieldValue(`subscriptionDetails.${planType}`, updatedPlans);
      }
    };

    cleanupTrialDuration(safeSubscriberPlans, "subscriberPlans");
    cleanupTrialDuration(safeEnterprisePlans, "enterprisePlans");
  }, [safeSubscriberPlans, safeEnterprisePlans, formik.setFieldValue]);

  useEffect(() => {
    if (!hasEnterprise || !Array.isArray(safeEnterprisePlans)) {
      return;
    }

    let needsUpdate = false;
    const normalizedPlans = safeEnterprisePlans.map((plan) => {
      const currentGroupIds = Array.isArray(plan.enterpriseGroupIds)
        ? plan.enterpriseGroupIds
        : [];
      const normalizedGroupIds = normalizeEnterpriseGroupIds(currentGroupIds);

      const hasSameLength =
        currentGroupIds.length === normalizedGroupIds.length;
      const hasSameValues = currentGroupIds.every(
        (id, idx) => id === normalizedGroupIds[idx],
      );

      if (hasSameLength && hasSameValues) {
        return plan;
      }

      needsUpdate = true;
      return { ...plan, enterpriseGroupIds: normalizedGroupIds };
    });

    if (needsUpdate) {
      formik.setFieldValue(
        "subscriptionDetails.enterprisePlans",
        normalizedPlans,
      );
    }
  }, [hasEnterprise, safeEnterprisePlans, formik.setFieldValue]);

  // Validation effect - use JSON.stringify to avoid object reference issues
  useEffect(() => {
    if (onValidationChange) {
      const isValid = validateSubscription();
      onValidationChange(isValid);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    JSON.stringify(subscriptionDetails),
    JSON.stringify(applicableTo),
    onValidationChange,
  ]);

  // Expose validation function for step 2 (active tab only)
  const validateActiveTab = () => {
    return validateSubscription(true);
  };

  // Also expose this function via ref or prop callback if needed
  useEffect(() => {
    if (onValidateActiveTab && showStepFooter) {
      // Provide the active tab validation function to parent
      onValidateActiveTab(validateActiveTab);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeTab,
    showSubscriberTab,
    showEnterpriseTab,
    JSON.stringify(subscriptionDetails),
    onValidateActiveTab,
    showStepFooter,
  ]);

  // Handle cost type change
  const handleCostTypeChange = (e) => {
    const value = Number(e.target.value);
    formik.setFieldValue("dirty", true);
    formik.setFieldValue("subscriptionDetails.costType", value);

    // If changing to free (1), clear all subscription plans
    if (value === 1) {
      formik.setFieldValue("subscriptionDetails.subscriberPlans", []);
      formik.setFieldValue("subscriptionDetails.enterprisePlans", []);
    }
  };

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Add new subscription row
  const addSubscriptionRow = (planType) => {
    const fieldName = `subscriptionDetails.${planType}`;
    const currentPlans =
      planType === "subscriberPlans"
        ? safeSubscriberPlans
        : safeEnterprisePlans;
    const isEnterprise = planType === "enterprisePlans";
    const newRow = getDefaultSubscriptionRow(isEnterprise);

    formik.setFieldValue("dirty", true);
    formik.setFieldValue(fieldName, [...currentPlans, newRow]);
  };

  // Delete subscription row
  const deleteSubscriptionRow = (planType, index) => {
    const currentPlans =
      planType === "subscriberPlans"
        ? safeSubscriberPlans
        : safeEnterprisePlans;

    // Prevent deletion if only one row remains
    if (currentPlans.length <= 1) {
      toast.warn("At least one subscription plan must remain.");
      return;
    }

    const fieldName = `subscriptionDetails.${planType}`;
    const updatedPlans = [...currentPlans];
    updatedPlans.splice(index, 1);

    formik.setFieldValue("dirty", true);
    formik.setFieldValue(fieldName, updatedPlans);
  };

  // Helper function to handle frequency changes
  const handleFrequencyChange = (planType, index, value) => {
    if (value === 99) {
      // One-time frequency: disable trial
      formik.setFieldValue(
        `subscriptionDetails.${planType}[${index}].enableTrial`,
        false,
      );
      formik.setFieldValue(
        `subscriptionDetails.${planType}[${index}].trialDurationDays`,
        null,
      );
    } else {
      // Recurring frequency: validate trial duration
      const currentPlan =
        formik.values.subscriptionDetails?.[planType]?.[index];
      const currentTrialDuration = currentPlan?.trialDurationDays;

      if (currentPlan?.enableTrial && currentTrialDuration) {
        const availableOptions = getTrialDurationOptions(value);
        const isCurrentDurationValid = availableOptions.some(
          (option) => option.value === currentTrialDuration,
        );

        if (!isCurrentDurationValid) {
          formik.setFieldValue(
            `subscriptionDetails.${planType}[${index}].trialDurationDays`,
            null,
          );
        }
      }
    }
  };

  const getEnterpriseCombinationError = (index) => {
    const plan = safeEnterprisePlans[index];

    if (!plan) {
      return null;
    }

    if (
      hasEnterpriseGroupFrequencyConflict(
        safeEnterprisePlans,
        index,
        plan.enterpriseGroupIds,
        plan.frequency,
      )
    ) {
      return "Frequency already exists for selected Enterprise Group(s).";
    }

    return null;
  };

  // Update subscription row field
  const updateSubscriptionField = (planType, index, field, value) => {
    const fieldPath = `subscriptionDetails.${planType}[${index}].${field}`;
    formik.setFieldValue("dirty", true);

    // Special handling for trialDurationDays to ensure proper type
    if (field === "trialDurationDays") {
      const numValue =
        value === "" || value === null || value === undefined
          ? null
          : Number(value);
      formik.setFieldValue(fieldPath, numValue);
    } else {
      formik.setFieldValue(fieldPath, value);
    }

    // If effectiveFromDate changes, mark effectiveToDate as touched for immediate validation
    if (field === "effectiveFromDate") {
      markFieldTouched(planType, index, "effectiveToDate");
    }

    // Handle frequency changes
    if (field === "frequency") {
      handleFrequencyChange(planType, index, value);
    }

    // If disabling trial, clear trial duration
    if (field === "enableTrial" && !value) {
      formik.setFieldValue(
        `subscriptionDetails.${planType}[${index}].trialDurationDays`,
        null, // Use null instead of empty string
      );
    }
  };

  // Check if trial is applicable for frequency
  const isTrialApplicable = (frequency) => {
    return RECURRING_FREQUENCIES.has(frequency);
  };

  // Check if delete is allowed for a row
  const canDeleteRow = (row, planType) => {
    const currentPlans =
      planType === "subscriberPlans"
        ? safeSubscriberPlans
        : safeEnterprisePlans;
    // Cannot delete if:
    // 1. Row has active subscriptions, OR
    // 2. Only one row remains (must always have at least 1 row)
    return !row.hasActiveSubscriptions && currentPlans.length > 1;
  };

  // Get tooltip message for delete button
  const getDeleteTooltipMessage = (plan, planType) => {
    if (canDeleteRow(plan, planType)) {
      return "Delete subscription row";
    }
    if (plan.hasActiveSubscriptions) {
      return "Cannot delete - active subscriptions exist";
    }
    return "Cannot delete - at least one plan must remain";
  };

  // Helper: Determine which tabs to validate based on active tab and available tabs
  const determineTabsToValidate = (validateActiveTabOnly) => {
    if (!validateActiveTabOnly) {
      return {
        validateSubscriberTab: hasSubscriber,
        validateEnterpriseTab: hasEnterprise,
      };
    }

    // When both tabs are available, validate based on active tab
    if (showSubscriberTab && showEnterpriseTab) {
      return {
        validateSubscriberTab: activeTab === 0,
        validateEnterpriseTab: activeTab === 1,
      };
    }

    // When only one tab is available, validate that tab
    return {
      validateSubscriberTab: showSubscriberTab,
      validateEnterpriseTab: showEnterpriseTab,
    };
  };

  // Helper: Validate all plans for a specific tab
  const validatePlansForTab = (plans, isEnterprise) => {
    return plans.every((plan) => validatePlan(plan, isEnterprise));
  };

  // Validate subscription configuration
  const validateSubscription = (validateActiveTabOnly = false) => {
    if (costType === 1) {
      return true;
    }

    const { validateSubscriberTab, validateEnterpriseTab } =
      determineTabsToValidate(validateActiveTabOnly);

    // Check if at least one plan exists for tabs being validated
    if (validateSubscriberTab && safeSubscriberPlans.length === 0) {
      return false;
    }

    if (validateEnterpriseTab && safeEnterprisePlans.length === 0) {
      return false;
    }

    // Validate subscriber plans
    if (
      validateSubscriberTab &&
      !validatePlansForTab(safeSubscriberPlans, false)
    ) {
      return false;
    }

    // Validate enterprise plans and group rules
    if (validateEnterpriseTab) {
      if (!validatePlansForTab(safeEnterprisePlans, true)) {
        return false;
      }
      if (!validateEnterpriseGroupRules()) {
        return false;
      }
    }

    return true;
  };

  // Validate individual plan using Yup schema
  const validatePlan = (plan, isEnterprise) => {
    const schema = isEnterprise
      ? EnterpriseSubscriptionPlanSchema
      : SubscriptionPlanSchema;

    return schema.isValidSync(plan, { abortEarly: false });
  };

  // Validate enterprise group rules
  const validateEnterpriseGroupRules = () => {
    return safeEnterprisePlans.every(
      (plan, index) =>
        !hasEnterpriseGroupFrequencyConflict(
          safeEnterprisePlans,
          index,
          plan.enterpriseGroupIds,
          plan.frequency,
        ),
    );
  };

  // Mark field as touched
  const markFieldTouched = (planType, index, field) => {
    const key = `${planType}-${index}-${field}`;
    if (!touchedFields[key]) {
      setTouchedFields((prev) => ({ ...prev, [key]: true }));
    }
  };

  // Check if field is touched
  const isFieldTouched = (planType, index, field) => {
    const key = `${planType}-${index}-${field}`;
    return touchedFields[key] === true;
  };

  // Get error for a field using Yup validation (only if touched)
  const getFieldError = (planType, index, field) => {
    // Only show error if field has been touched
    if (!isFieldTouched(planType, index, field)) {
      return null;
    }

    const plans =
      planType === "subscriberPlans"
        ? safeSubscriberPlans
        : safeEnterprisePlans;
    const plan = plans[index];

    if (!plan) return null;

    const isEnterprise = planType === "enterprisePlans";
    const schema = isEnterprise
      ? EnterpriseSubscriptionPlanSchema
      : SubscriptionPlanSchema;

    try {
      schema.validateSyncAt(field, plan);
      return null;
    } catch (error) {
      return error.message;
    }
  };

  // Render subscription row
  const renderSubscriptionRow = (plan, index, planType, addButtonInfo) => {
    const isEnterprise = planType === "enterprisePlans";
    const isNewRow = !plan.addInSubscriptionId;
    const isDisabled =
      !isEditMode ||
      plan.hasActiveSubscriptions ||
      (isPublished && !isNewRow);
    const isEffectiveToDateDisabled = !isEditMode || plan.hasActiveSubscriptions;
    const enterpriseCombinationError =
      isEnterprise &&
        (isFieldTouched(planType, index, "enterpriseGroupIds") ||
          isFieldTouched(planType, index, "frequency"))
        ? getEnterpriseCombinationError(index)
        : null;
    const currentPlans =
      planType === "subscriberPlans"
        ? safeSubscriberPlans
        : safeEnterprisePlans;
    const frequencyOptions = isEnterprise
      ? getAvailableEnterpriseFrequencyOptions(
        safeEnterprisePlans,
        index,
        plan.frequency,
        plan.enterpriseGroupIds,
      )
      : getAvailableFrequencyOptions(currentPlans, index, plan.frequency);

    return (
      <Box
        key={plan.id || index}
        className="subscription-row"
        sx={{
          backgroundColor: "#f8f9fa",
          borderRadius: 1,
          p: 2,
          mb: 2,
          // border: "1px solid #e0e0e0",
        }}
      >
        <Grid container spacing={2} alignItems="flex-start">
          {/* Enterprise Group Selection */}
          {isEnterprise && (
            <Grid item xs={12} md={12}>
              <Box
                display="flex"
                flexDirection={"column"}
                alignItems="flex-start"
                mb={1}
                width={"400px"}
              >
                <label htmlFor={`enterprise-groups-${index}`}>
                  Select Enterprise Group(s){" "}
                  <span className="required-asterisk">*</span>
                </label>
                <MultiSelectDropDown
                  id={`enterprise-groups-${index}`}
                  name={`enterprise-groups-${index}`}
                  options={getEnterpriseGroupOptionsForRow(
                    plan.enterpriseGroupIds,
                    index,
                    currentPlans,
                    plan.frequency,
                  )}
                  value={normalizeEnterpriseGroupIds(
                    plan.enterpriseGroupIds,
                  ).map((id) => {
                    const allOptions = getEnterpriseGroupOptionsForRow(
                      plan.enterpriseGroupIds,
                      index,
                      currentPlans,
                      plan.frequency,
                    );
                    return (
                      allOptions.find((option) => option.value === id) || {
                        value: id,
                        label: id,
                      }
                    );
                  })}
                  onChange={(selected) =>
                    handleEnterpriseGroupSelection(
                      planType,
                      index,
                      selected.map((option) => option.value),
                    )
                  }
                  onInputChange={handleEnterpriseGroupSearch}
                  onBlur={() =>
                    markFieldTouched(planType, index, "enterpriseGroupIds")
                  }
                  isLoading={isSearchingGroups}
                  filterOption={() => true}
                  disabled={isDisabled}
                  placeholder="Search and Select Enterprise Groups"
                />
                {getFieldError(planType, index, "enterpriseGroupIds") && (
                  <InlineMessage
                    error={getFieldError(planType, index, "enterpriseGroupIds")}
                  />
                )}
              </Box>
            </Grid>
          )}

          {/* Frequency */}
          <Grid item xs={12} sm={6} md={2.4}>
            <label htmlFor={`frequency-${planType}-${index}`}>
              Frequency <span className="required-asterisk">*</span>
            </label>
            <select
              id={`frequency-${planType}-${index}`}
              name={`frequency-${planType}-${index}`}
              value={plan.frequency || ""}
              onChange={(e) =>
                updateSubscriptionField(
                  planType,
                  index,
                  "frequency",
                  Number(e.target.value),
                )
              }
              onBlur={() => markFieldTouched(planType, index, "frequency")}
              className="form-control form-select"
              disabled={isDisabled}
            >
              <option value="">Select Frequency</option>
              {frequencyOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {getFieldError(planType, index, "frequency") && (
              <InlineMessage
                error={getFieldError(planType, index, "frequency")}
              />
            )}
            {!getFieldError(planType, index, "frequency") &&
              enterpriseCombinationError && (
                <InlineMessage error={enterpriseCombinationError} />
              )}
          </Grid>

          {/* Base Cost */}
          <Grid item xs={12} sm={6} md={2.4}>
            <label htmlFor={`baseValue-${planType}-${index}`}>
              Base Cost <span className="required-asterisk">*</span>
            </label>
            <CurrencyTextbox
              id={`baseValue-${planType}-${index}`}
              name={`baseValue-${planType}-${index}`}
              value={plan.baseValue}
              onChange={(value) =>
                updateSubscriptionField(planType, index, "baseValue", value)
              }
              onBlur={() => markFieldTouched(planType, index, "baseValue")}
              disabled={isDisabled}
              type="text"
              fontSize="14px"
            />
            {getFieldError(planType, index, "baseValue") && (
              <InlineMessage
                error={getFieldError(planType, index, "baseValue")}
              />
            )}
          </Grid>

          {/* Effective From Date */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2.4}
            className="date-picker-matui desktop-date-picker"
          >
            <label htmlFor={`effectiveFromDate-${planType}-${index}`}>
              Effective From Date <span className="required-asterisk">*</span>
            </label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                disablePast
                className="form-label form-control date-field"
                inputFormat="MM/DD/YYYY"
                value={plan.effectiveFromDate || null}
                onChange={(date) =>
                  updateSubscriptionField(
                    planType,
                    index,
                    "effectiveFromDate",
                    date,
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={`effectiveFromDate-${planType}-${index}`}
                    size="small"
                    fullWidth
                    onBlur={() =>
                      markFieldTouched(planType, index, "effectiveFromDate")
                    }
                  />
                )}
                disabled={isDisabled}
              />
            </LocalizationProvider>
            {getFieldError(planType, index, "effectiveFromDate") && (
              <InlineMessage
                error={getFieldError(planType, index, "effectiveFromDate")}
              />
            )}
          </Grid>

          {/* Effective To Date */}
          <Grid
            item
            xs={12}
            sm={6}
            md={2.4}
            className="date-picker-matui desktop-date-picker"
          >
            <label htmlFor={`effectiveToDate-${planType}-${index}`}>
              Effective To Date
            </label>
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DesktopDatePicker
                minDate={plan.effectiveFromDate || undefined}
                className="form-label form-control date-field"
                inputFormat="MM/DD/YYYY"
                value={plan.effectiveToDate || null}
                onChange={(date) =>
                  updateSubscriptionField(
                    planType,
                    index,
                    "effectiveToDate",
                    date,
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    id={`effectiveToDate-${planType}-${index}`}
                    size="small"
                    fullWidth
                    onBlur={() =>
                      markFieldTouched(planType, index, "effectiveToDate")
                    }
                  />
                )}
                disabled={isEffectiveToDateDisabled}
              />
            </LocalizationProvider>
            {getFieldError(planType, index, "effectiveToDate") && (
              <InlineMessage
                error={getFieldError(planType, index, "effectiveToDate")}
              />
            )}
          </Grid>

          {/* Auto Renewal */}
          {plan?.frequency !== 99 && (
            <Grid item xs={12} sm={6} md={2.4}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={plan?.isRecurring || false}
                    onChange={(e) =>
                      updateSubscriptionField(
                        planType,
                        index,
                        "isRecurring",
                        e.target.checked,
                      )
                    }
                    disabled={isDisabled}
                  />
                }
                label="Auto Renewal"
                sx={{ mt: 3.9 }}
              />
            </Grid>
          )}

          {/* Bottom Row: Trial Configuration (left) + Delete Button (right) */}
           
          <Grid item xs={12}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mt: 1,
                pt: 2,
                //borderTop: "1px dashed #e0e0e0",
              }}
            >
              {/* Trial Configuration - Left Side */}
              <Box sx={{ display: "flex", alignItems: "self-end", gap: 2 }}>
                {plan?.isRecurring && isTrialApplicable(plan.frequency) && (
                  <>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={plan.enableTrial}
                          onChange={(e) =>
                            updateSubscriptionField(
                              planType,
                              index,
                              "enableTrial",
                              e.target.checked,
                            )
                          }
                          disabled={isDisabled || plan.hasActiveSubscriptions}
                        />
                      }
                      label="Enable Trial"
                    />
                    {plan.hasActiveSubscriptions && plan.enableTrial && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        display="block"
                      >
                        Trial config cannot be modified after purchase
                      </Typography>
                    )}

                    {/* Trial Duration - Visible if Enable Trial is checked */}
                    {plan.enableTrial && (
                      <Box sx={{ minWidth: 150 }}>
                        <label
                          htmlFor={`trialDurationDays-${planType}-${index}`}
                        >
                          Trial Duration{" "}
                          <span className="required-asterisk">*</span>
                        </label>
                        <select
                          id={`trialDurationDays-${planType}-${index}`}
                          name={`trialDurationDays-${planType}-${index}`}
                          value={plan.trialDurationDays || ""}
                          onChange={(e) =>
                            updateSubscriptionField(
                              planType,
                              index,
                              "trialDurationDays",
                              Number(e.target.value),
                            )
                          }
                          onBlur={() =>
                            markFieldTouched(
                              planType,
                              index,
                              "trialDurationDays",
                            )
                          }
                          className="form-control form-select"
                          disabled={isDisabled || plan.hasActiveSubscriptions}
                        >
                          <option value="">Select Duration</option>
                          {getTrialDurationOptions(plan.frequency).map(
                            (option) => (
                              <option key={option.value} value={option.value}>
                                {option.label}
                              </option>
                            ),
                          )}
                        </select>
                        {getFieldError(
                          planType,
                          index,
                          "trialDurationDays",
                        ) && (
                            <InlineMessage
                              error={getFieldError(
                                planType,
                                index,
                                "trialDurationDays",
                              )}
                            />
                          )}
                      </Box>
                    )}
                  </>
                )}
              </Box>

              {/* Delete Button - Right Side */}
              <Box display="flex" alignItems="end" gap={2}>
                {addButtonInfo?.length === index + 1 &&
                  isEditMode &&
                  costType === 2 && (
                    <Tooltip title={addButtonInfo?.toooltipTitle}>
                      <span>
                        <IconButton
                          onClick={() => addSubscriptionRow(planType)}
                          disabled={!addButtonInfo?.canAddMore}
                          sx={{
                            mt: 1,
                            borderRadius: 1,
                            backgroundColor: addButtonInfo?.canAddMore
                              ? "#1aaefa"
                              : "#e0e0e0",
                            color: addButtonInfo?.canAddMore
                              ? "white"
                              : "#9e9e9e",
                            "&:hover": {
                              backgroundColor: addButtonInfo?.canAddMore
                                ? "#0d8fd4"
                                : "#e0e0e0",
                            },
                            "&:disabled": {
                              backgroundColor: "#e0e0e0",
                              color: "#9e9e9e",
                            },
                          }}
                        >
                          <AddOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                <Tooltip title={getDeleteTooltipMessage(plan, planType)}>
                  <span>
                    <IconButton
                      onClick={() => deleteSubscriptionRow(planType, index)}
                      disabled={
                        !isEditMode ||
                        !canDeleteRow(plan, planType) ||
                        plan.addInSubscriptionId > 0
                      }
                      sx={{
                        borderRadius: 1,
                        backgroundColor: "#d32f2f",
                        color: "white",
                        "&:hover": { backgroundColor: "#b71c1c" },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Box>
          </Grid>          
        </Grid>
      </Box>
    );
  };

  // Render subscription tab content
  const renderTabContent = (planType, plans) => {
    const canAddMore =
      planType === "enterprisePlans" ? true : canAddMoreRows(plans);
    const addButtonTooltip = canAddMore
      ? "Add Subscription Plan"
      : "All frequency options are already used";

    const addButtonInfo = {
      toooltipTitle: addButtonTooltip,
      canAddMore: canAddMore,
      length: plans.length,
    };
    return (
      <Box sx={{ pt: 2, padding: 2 }} className="subscription-tab-content">
        {plans.length === 0 && costType === 2 && (
          <Typography color="error" sx={{ mb: 2 }}>
            At least one subscription plan is required for Paid cost type.
          </Typography>
        )}

        {plans.map((plan, index) =>
          renderSubscriptionRow(plan, index, planType, addButtonInfo),
        )}
      </Box>
    );
  };

  return (
    <Accordion
      className="accordion-ent"
      expanded={expanded === "subscriptionPanel"}
      onChange={onExpandChange("subscriptionPanel")}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls="subscription-panel-content"
        //id="subscription-panel-header"
        id="panel2a-header"
      >
        <Typography sx={{ fontWeight: "700" }}>Subscription Details</Typography>
      </AccordionSummary>
      <AccordionDetails className="accordion-basic-details">
        <Grid container spacing={2}>
          {/* Cost Type Dropdown */}
          <Grid item xs={12} md={3}>
            <label htmlFor="costType">
              Cost Type <span className="required-asterisk">*</span>
            </label>
            <select
              id="costType"
              name="costType"
              value={costType}
              onChange={handleCostTypeChange}
              className="form-control form-select"
              disabled={!isEditMode || isPublished}
            >
              {COST_TYPE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.name}
                </option>
              ))}
            </select>
          </Grid>
        </Grid>

        {/* Subscription Configuration - Only show if Paid */}
        {costType === 2 && (
          <Box sx={{ mt: 3 }}>
            {/* Show tabs only if applicable */}
            {(showSubscriberTab || showEnterpriseTab) && (
              <>
                <Box sx={{ borderBottom: 0, borderColor: "divider" }}>
                  <Tabs
                    value={activeTab}
                    onChange={handleTabChange}
                    aria-label="subscription tabs"
                    sx={{
                      "& .MuiTab-root": {
                        textTransform: "capitalize",
                        backgroundColor: "rgba(254, 247, 255, 1)",
                      },
                      "& .MuiTab-root.Mui-selected": {
                        backgroundColor: "rgba(121, 116, 126, 1)",
                        color: "white",
                      },
                      "& .MuiTabs-indicator": {
                        display: "none",
                      },
                    }}
                  >
                    {showSubscriberTab && (
                      <Tab
                        label="Subscriber"
                        id="subscription-tab-0"
                        aria-controls="subscription-tabpanel-0"
                      />
                    )}
                    {showEnterpriseTab && (
                      <Tab
                        label="Enterprise"
                        id={
                          showSubscriberTab
                            ? "subscription-tab-1"
                            : "subscription-tab-0"
                        }
                        aria-controls={
                          showSubscriberTab
                            ? "subscription-tabpanel-1"
                            : "subscription-tabpanel-0"
                        }
                      />
                    )}
                  </Tabs>
                </Box>

                {/* Subscriber Tab */}
                {showSubscriberTab && (
                  <TabPanel
                    value={activeTab}
                    index={0}
                    key="subscriber-tab"
                    className="subscription-tab-panel"
                  >
                    {renderTabContent("subscriberPlans", safeSubscriberPlans)}
                  </TabPanel>
                )}

                {/* Enterprise Tab */}
                {showEnterpriseTab && (
                  <TabPanel
                    value={activeTab}
                    index={showSubscriberTab ? 1 : 0}
                    key="enterprise-tab"
                    className="subscription-tab-panel"
                  >
                    {renderTabContent("enterprisePlans", safeEnterprisePlans)}
                  </TabPanel>
                )}
              </>
            )}

            {/* No applicable to selected message */}
            {!showSubscriberTab && !showEnterpriseTab && (
              <Box
                sx={{
                  mt: 2,
                  p: 2,
                  backgroundColor: "#f5f5f5",
                  borderRadius: 1,
                  textAlign: "center",
                }}
              >
                <Typography color="text.secondary">
                  Please select &quot;Applicable To&quot; in Basic Information
                  to configure subscription plans.
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {/* Free cost type message */}
        {costType === 1 && (
          <Box
            sx={{
              mt: 2,
              p: 2,
              backgroundColor: "#e8f5e9",
              borderRadius: 1,
            }}
          >
            <Typography color="text.secondary">
              No subscription configuration required for Free add-ins.
            </Typography>
          </Box>
        )}

        {/* Step 2 Footer Buttons */}
        {showStepFooter && (
          <Grid container justifyContent="flex-end" sx={{ mt: 2, mb: 1 }}>
            <Grid item>
              <FooterButtons {...stepFooterProps} />
            </Grid>
          </Grid>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

SubscriptionAddInSection.propTypes = {
  formik: PropTypes.shape({
    values: PropTypes.object.isRequired,
    setFieldValue: PropTypes.func.isRequired,
    errors: PropTypes.object,
    touched: PropTypes.object,
  }).isRequired,
  isEditMode: PropTypes.bool,
  isPublished: PropTypes.bool,
  hasActiveSubscriptions: PropTypes.bool,
  enterpriseGroupOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      label: PropTypes.string,
    }),
  ),
  searchEnterpriseGroups: PropTypes.func,
  onValidationChange: PropTypes.func,
  onValidateActiveTab: PropTypes.func,
  expanded: PropTypes.string,
  onExpandChange: PropTypes.func,
  showStepFooter: PropTypes.bool,
  stepFooterProps: PropTypes.object,
};

SubscriptionAddInSection.defaultProps = {
  isEditMode: true,
  isPublished: false,
  hasActiveSubscriptions: false,
  enterpriseGroupOptions: [],
  searchEnterpriseGroups: null,
  onValidationChange: null,
  onValidateActiveTab: null,
  expanded: "",
  onExpandChange: () => () => { },
  showStepFooter: false,
  stepFooterProps: {},
};

export default SubscriptionAddInSection;
